import React, { useState, useCallback } from 'react';
import { klineSignalService } from '../services/klineSignalService';
import { BarInterval } from '@/services/enums';

interface KlineMetricsDisplayProps {
  symbol: string;
  bar: BarInterval;
  visibleRange: { from: number; to: number } | null; // 时间戳（秒）
}

interface MetricsData {
  symbol: string;
  bar: string;
  startTime: number;
  endTime: number;
  candleCount: number;
  metrics: {
    priceSlope: number;
    rsiSlope: number;
    volumeSlope: number;
    rsiValue: number;
    maxVolatility: number;
    minVolatility: number;
    avgVolatility: number;
    volatilities: number[];
    avgVolume: number;
  };
  parameters: {
    rsiPeriod: number;
    lookbackWindow: number;
  };
}

const KlineMetricsDisplay: React.FC<KlineMetricsDisplayProps> = ({ symbol, bar, visibleRange }) => {
  const [loading, setLoading] = useState(false);
  const [metricsData, setMetricsData] = useState<MetricsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 格式化时间戳为北京时间
  // timestamp 可能是毫秒级或秒级，统一转换为毫秒级处理
  const formatTimestamp = (timestamp: number): string => {
    // 如果时间戳小于 1e12，认为是秒级，需要转换为毫秒级
    const msTimestamp = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    const date = new Date(msTimestamp);
    const beijingTime = new Date(date.getTime() + 8 * 60 * 60 * 1000);
    return beijingTime.toISOString().replace('T', ' ').substring(0, 19);
  };

  // 获取指标数据
  const fetchMetrics = useCallback(async () => {
    if (!symbol || !visibleRange || !visibleRange.from || !visibleRange.to) {
      setMetricsData(null);
      return;
    }

    // 确保时间范围有效
    if (visibleRange.from >= visibleRange.to) {
      setMetricsData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await klineSignalService.calculateMetrics({
        symbol,
        startTime: visibleRange.from,
        endTime: visibleRange.to,
        bar,
      });

      if (response) {
        setMetricsData(response);
      } else {
        throw new Error('获取指标数据失败');
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || '获取指标数据失败';
      setError(errorMsg);
      setMetricsData(null);
      console.error('获取指标数据失败:', err);
    } finally {
      setLoading(false);
    }
  }, [symbol, bar, visibleRange]);

  // 不再自动获取指标数据，只有点击刷新按钮时才调用

  // 如果没有可见范围，不显示组件
  if (!visibleRange || !visibleRange.from || !visibleRange.to) {
    return null;
  }

  return (
    <div>
      <div className="px-4 py-4">
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-white">📊 视口指标分析</h2>
            <button
              onClick={fetchMetrics}
              disabled={loading}
              className="px-3 py-1.5 bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>加载中...</span>
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span>刷新</span>
                </>
              )}
            </button>
          </div>
        </div>

        {loading && !metricsData && <div className="text-center py-4 text-white/60 text-sm">正在计算指标...</div>}

        {error && <div className="text-red-400 text-sm py-2">{error}</div>}

        {metricsData && !loading && (
          <div className="space-y-3">
            {/* 时间范围信息 */}
            <div className="text-xs text-white/60 space-y-1">
              <div>
                时间范围: {formatTimestamp(metricsData.startTime)} ~ {formatTimestamp(metricsData.endTime)}
              </div>
              <div>
                K线数量: {metricsData.candleCount} 根 | 时间粒度: {metricsData.bar}
              </div>
              <div>
                参数: RSI周期={metricsData.parameters.rsiPeriod}, 回看窗口={metricsData.parameters.lookbackWindow}
              </div>
            </div>

            {/* 指标数据 */}
            <div className="grid grid-cols-2 gap-2">
              {/* 价格斜率 */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">价格斜率</div>
                <div
                  className={`text-sm font-semibold ${
                    metricsData.metrics.priceSlope > 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {metricsData.metrics.priceSlope.toFixed(4)}
                </div>
              </div>

              {/* RSI斜率 */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">RSI斜率</div>
                <div
                  className={`text-sm font-semibold ${
                    metricsData.metrics.rsiSlope > 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {metricsData.metrics.rsiSlope.toFixed(4)}
                </div>
              </div>

              {/* 成交量斜率 */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">成交量斜率</div>
                <div
                  className={`text-sm font-semibold ${
                    metricsData.metrics.volumeSlope > 0 ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {metricsData.metrics.volumeSlope.toFixed(4)}
                </div>
              </div>

              {/* 平均成交量 */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">平均成交量</div>
                <div className="text-sm font-semibold text-white">
                  {metricsData.metrics.avgVolume.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>

              {/* 最大波动率 */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">最大波动率</div>
                <div className="text-sm font-semibold text-white">
                  {(metricsData.metrics.maxVolatility * 100).toFixed(2)}%
                </div>
              </div>

              {/* 平均波动率 */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">平均波动率</div>
                <div className="text-sm font-semibold text-white">
                  {(metricsData.metrics.avgVolatility * 100).toFixed(2)}%
                </div>
              </div>
            </div>

            {/* RSI值和波动率范围 */}
            <div className="grid grid-cols-2 gap-2">
              {/* RSI值 */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">RSI值</div>
                <div
                  className={`text-sm font-semibold ${
                    metricsData.metrics.rsiValue > 70
                      ? 'text-red-400'
                      : metricsData.metrics.rsiValue < 30
                        ? 'text-green-400'
                        : 'text-white'
                  }`}
                >
                  {metricsData.metrics.rsiValue.toFixed(2)}
                </div>
              </div>

              {/* 波动率范围 */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">波动率范围</div>
                <div className="text-sm text-white">
                  {(metricsData.metrics.minVolatility * 100).toFixed(2)}% ~{' '}
                  {(metricsData.metrics.maxVolatility * 100).toFixed(2)}%
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KlineMetricsDisplay;
